import { Avatar, IconButton } from '@mui/material';
import { MoreVert as MoreVertIcon, KeyboardArrowDown as KeyboardArrowDownIcon } from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import { useEffect, useMemo, useRef, useState } from 'react';
import {
  QButton,
  QTable,
  QTableHead,
  QTableRow,
  QTableBody,
  QTableCell,
  QTableSortableColumnHeader,
  QSearchBar,
  QAsyncTablePagination,
  QSelect,
} from '@kw/quokka-ui';
// components

// pagination
import { useQPagination } from '../../../kw-ui-components/Pagination/useQPagination';

import { useLocalStorage } from '../../../utils/useLocalStorage';

import { useFetchService } from '../../../utils/fetchService';
import { useAppManagementSortParams } from '../useAppManagementSortParams';
import { PageLoader } from '../../../PageLoader';
import { ResetButton } from '../../shared/ResetButton';
import { getComparator } from '../../../utils/getComparator';
import { createQueryString } from '../../../utils/createQueryString';
import { formatPlatform } from '../../../utils/formatPlatform';
import { ManagedAppsTableAddAppPopover, AppManagementTableMorePopover } from '../AppManagementPopovers';
import { ManagedAppsModal } from './ManagedAppsModals';
import { handleUserSelection } from '../../../utils/handleUserSelection';
import { getPlatformFilterValue, hasFilterActive } from '../../../utils/filterHelpers';

export const useManagedApps = (queryParams, accessToken) => {
  const queryParamString = createQueryString(queryParams);
  const { data: managedAppsData, error: managedAppsError, mutate } = useFetchService(`/pdm/managed-apps?${queryParamString}`, accessToken);
  const isLoading = !managedAppsData && !managedAppsError;
  return { managedAppsData, managedAppsError, isLoading, mutate };
};

export const useTrustedApps = accessToken => {
  const { data: trustedAppsData, error: trustedAppsError, mutate } = useFetchService(`/list/trusted-apps`, accessToken);
  const isLoading = !trustedAppsData && !trustedAppsError;
  return { trustedAppsData, trustedAppsError, isLoading, mutate };
};

export default function ManagedAppsTable() {
  const [accessToken = ''] = useLocalStorage('accessToken', '');
  const [searchQuery, setSearchQuery] = useState('');
  const [vendorData, setVendorData] = useState([]);
  const [appId, setAppId] = useState(null);
  const [moreButtonAnchorEl, setMoreButtonAnchorEl] = useState(null);
  const [addAppButtonAnchorEl, setAddAppButtonAnchorEl] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [modalAppInfo, setModalAppInfo] = useState({});
  const [modalCategory, setModalCategory] = useState('remove');
  const { page, pageSize, handleChangePage, handleChangeRowsPerPage, setPage } = useQPagination(10);
  const [selectedPlatforms, setSelectedPlatforms] = useState(['all']);
  const [selectedVendor, setSelectedVendor] = useState(['all']);
  const [selectedPublished, setSelectedPublished] = useState(['all']);

  const {
    appNameOrder,
    toggleAppNameOrder,
    platformOrder,
    togglePlatformOrder,
    vendorOrder,
    toggleVendorOrder,
    versionOrder,
    toggleVersionOrder,
    appIdOrder,
    toggleAppIdOrder,
    numberOfDevicesOrder,
    toggleNumberOfDevicesOrder,
    publishedOrder,
    togglePublishedOrder,
    orderBy,
    setOrderBy,
    resetToggles,
  } = useAppManagementSortParams();

  const os = getPlatformFilterValue(selectedPlatforms);

  // eslint-disable-next-line prefer-const
  let { managedAppsData, managedAppsError, isLoading, mutate } = useManagedApps(
    {
      os,
    },
    accessToken,
  );

  const { trustedAppsData, mutate: mutateTrustedAppsData } = useTrustedApps(accessToken);

  const filteredManagedApps = useMemo(() => {
    const apps = managedAppsData || [];
    const query = searchQuery.toLowerCase();
    const hasPlatformFilter = hasFilterActive(selectedPlatforms);
    const hasVendorFilter = hasFilterActive(selectedVendor);
    const hasPublishedFilter = hasFilterActive(selectedPublished);
    const filterConditions = searchQuery.length || hasPlatformFilter || hasVendorFilter || hasPublishedFilter;

    return filterConditions
      ? apps
          .filter(
            item =>
              item.name.toLowerCase().includes(query) ||
              item.platform.toLowerCase().includes(query) ||
              item.vendorName.toLowerCase().includes(query) ||
              item.version.toLowerCase().includes(query) ||
              item.packageName.toLowerCase().includes(query),
          )
          .filter(item => {
            let platformFilter = true;
            let vendorFilter = true;
            let publishedFilter = true;

            if (hasPlatformFilter) {
              platformFilter = selectedPlatforms.includes(item.platform);
            }

            if (hasVendorFilter) {
              vendorFilter = selectedVendor.includes(item.vendorName);
            }

            if (hasPublishedFilter) {
              publishedFilter = selectedPublished.includes(String(item.published));
            }

            return platformFilter && vendorFilter && publishedFilter;
          })
      : apps;
  }, [managedAppsData, searchQuery, selectedPublished, selectedVendor, selectedPlatforms]);

  const total = filteredManagedApps?.length;
  const totalPageCount = Math.ceil(total / pageSize);

  const tableHeaders = [
    {
      title: 'App Name',
      onClick: toggleAppNameOrder,
      sortValue: appNameOrder,
    },
    {
      title: 'Platform',
      onClick: togglePlatformOrder,
      sortValue: platformOrder,
    },
    {
      title: 'Vendor',
      onClick: toggleVendorOrder,
      sortValue: vendorOrder,
    },
    {
      title: 'Version',
      onClick: toggleVersionOrder,
      sortValue: versionOrder,
    },
    {
      title: 'App ID',
      onClick: toggleAppIdOrder,
      sortValue: appIdOrder,
    },
    {
      title: '# of Devices',
      onClick: toggleNumberOfDevicesOrder,
      sortValue: numberOfDevicesOrder,
    },
    {
      title: 'Published',
      onClick: togglePublishedOrder,
      sortValue: publishedOrder,
    },
    {
      // kebab icon column
      title: '',
      onClick: () => {},
      sortValue: null,
    },
  ];

  // column sorting UI filters
  const order = tableHeaders.map(({ sortValue }) => sortValue).find(o => o);

  useEffect(() => {
    if (appNameOrder) {
      setOrderBy('name');
    }

    if (platformOrder) {
      setOrderBy('platform');
    }

    if (vendorOrder) {
      setOrderBy('vendorName');
    }

    if (versionOrder) {
      setOrderBy('version');
    }

    if (appIdOrder) {
      setOrderBy('packageName');
    }

    if (numberOfDevicesOrder) {
      setOrderBy('deviceInstallCount');
    }

    if (publishedOrder) {
      setOrderBy('published');
    }

    if (!order) {
      setOrderBy(null);
    }
  }, [
    numberOfDevicesOrder,
    order,
    orderBy,
    setOrderBy,
    appNameOrder,
    platformOrder,
    vendorOrder,
    versionOrder,
    appIdOrder,
    publishedOrder,
  ]);

  useEffect(() => {
    setPage(0);
  }, [searchQuery, setPage]);

  const platformSelectData = [
    { value: 'all', label: 'Both' },
    { value: 'android', label: 'Android' },
    { value: 'ios', label: 'iOS' },
  ];

  const publishedSelectData = [
    { value: 'all', label: 'Both' },
    { value: 'true', label: 'Yes' },
    { value: 'false', label: 'No' },
  ];

  const vendorSelectData = useRef([]);
  useEffect(() => {
    const appData = managedAppsData || [];
    vendorSelectData.current = [
      { value: 'all', label: 'All' },
      ...appData.map(({ vendorName }) => ({ value: vendorName, label: vendorName })),
    ];
    setVendorData([...new Map(vendorSelectData.current.map(item => [item.value, item])).values()]);
  }, [filteredManagedApps, managedAppsData]);

  const resetFilterAndSorts = () => {
    resetToggles();
    setPage(0);
    setSelectedPlatforms(['all']);
    setSelectedVendor(['all']);
    setSelectedPublished(['all']);
    setSearchQuery('');
  };

  const handleMoreButtonPopoverClick = (
    event: { currentTarget: HTMLButtonElement | HTMLAnchorElement } = null,
    managedAppId: string = null,
  ) => {
    if (!event && !managedAppId) {
      setAppId(null);
      setMoreButtonAnchorEl(null);
    } else {
      setAppId(managedAppId);
      setMoreButtonAnchorEl(event.currentTarget);
    }
  };

  const handleAddAppPopoverClick = event => {
    setAddAppButtonAnchorEl(addAppButtonAnchorEl ? null : event.currentTarget);
  };

  if (managedAppsError) {
    return <div>failed to load</div>;
  }

  return (
    <>
      {isLoading && <PageLoader />}
      <StyledFilterWrapper>
        <QSelect
          multiple
          label="Platform"
          options={platformSelectData}
          value={selectedPlatforms}
          onChange={e => {
            handleUserSelection(selectedPlatforms, e.target.value, setSelectedPlatforms);
            setPage(0);
          }}
          formControlProps={{ sx: { width: 110 } }}
        />
        <QSelect
          multiple
          label="Vendor"
          options={vendorData}
          value={selectedVendor}
          onChange={e => {
            handleUserSelection(selectedVendor, e.target.value, setSelectedVendor);
            setPage(0);
          }}
          formControlProps={{ sx: { width: 100 } }}
        />
        <QSelect
          multiple
          label="Published"
          options={publishedSelectData}
          value={selectedPublished}
          onChange={e => {
            handleUserSelection(selectedPublished, e.target.value, setSelectedPublished);
            setPage(0);
          }}
          formControlProps={{ sx: { width: 120 } }}
        />
        <ResetButton
          onClick={() => {
            resetFilterAndSorts();
          }}
        />
        <StyledQButton endIcon={<KeyboardArrowDownIcon />} variant="filled" onClick={event => handleAddAppPopoverClick(event)}>
          Add Managed App
        </StyledQButton>
        <ManagedAppsTableAddAppPopover
          anchorEl={addAppButtonAnchorEl}
          setAnchorEl={setAddAppButtonAnchorEl}
          handleClose={event => handleAddAppPopoverClick(event)}
        />
      </StyledFilterWrapper>
      <StyledTableWrapper>
        <QSearchBar
          searchValue={searchQuery}
          onChange={e => setSearchQuery(e.target.value)}
          placeholder="Search apps"
          onKeyPress={e => {
            if (e.key === 'Enter') {
              setSearchQuery(e.target.value.toString());
              setPage(0);
            }
          }}
          onRemove={() => {
            setSearchQuery('');
            setPage(0);
          }}
        />
        {total > 10 ? (
          <QAsyncTablePagination
            page={page + 1}
            pagination={{
              dataPerPage: pageSize,
              totalData: total,
              lastPage: totalPageCount,
            }}
            getPage={newPage => setPage(newPage - 1)}
            showRowsPerPage={true}
            currentRows={(page + 1) * pageSize > filteredManagedApps.length ? filteredManagedApps.length % pageSize : pageSize}
            handlePageSizeChange={handleChangeRowsPerPage}
            pageSize={pageSize}
            pageSizeOptions={[10, 20, 50, 100].map(v => ({ label: v, value: v }))}
          />
        ) : null}
        <QTable aria-label="Managed-Apps-Table">
          <QTableHead>
            <QTableRow>
              {tableHeaders.map(({ title, onClick, sortValue }) => (
                <QTableSortableColumnHeader
                  key={title}
                  title={title}
                  onClick={() => {
                    onClick();
                    setPage(0);
                  }}
                  sortValue={sortValue}
                />
              ))}
            </QTableRow>
          </QTableHead>
          <QTableBody>
            {[...filteredManagedApps]
              .sort(getComparator(order, orderBy))
              .slice(page * pageSize, (page + 1) * pageSize)
              .map(app => {
                return (
                  <QTableRow key={app.managedAppId} aria-label={`${app.name} row`}>
                    <StyledKwTableCell>
                      <StyledAvatar alt={`${app.name} app icon`} src={app.iconUrl} />
                      {app.name}
                    </StyledKwTableCell>
                    <QTableCell>{formatPlatform(app.platform)}</QTableCell>
                    <QTableCell>{app.vendorName}</QTableCell>
                    <QTableCell>{app.version}</QTableCell>
                    <QTableCell>{app.packageName}</QTableCell>
                    <QTableCell>{app.deviceInstallCount}</QTableCell>
                    <QTableCell>{app.published ? 'Yes' : 'No'}</QTableCell>
                    <QTableCell>
                      <IconButton size="medium" onClick={event => handleMoreButtonPopoverClick(event, app.managedAppId)}>
                        <MoreVertIcon />
                      </IconButton>
                    </QTableCell>
                    <AppManagementTableMorePopover
                      app={app}
                      appId={appId}
                      anchorEl={moreButtonAnchorEl}
                      setAppId={setAppId}
                      setAnchorEl={setMoreButtonAnchorEl}
                      handleClose={() => handleMoreButtonPopoverClick()}
                      setShowModal={setShowModal}
                      setModalAppInfo={setModalAppInfo}
                      setModalCategory={setModalCategory}
                      mutate={mutate}
                    />
                  </QTableRow>
                );
              })}
            <ManagedAppsModal
              isOpen={showModal}
              onClose={() => setShowModal(false)}
              app={modalAppInfo}
              category={modalCategory}
              mutateManagedAppsData={mutate}
              trustedApps={trustedAppsData?.trustedApps || []}
              mutateTrustedAppsData={mutateTrustedAppsData}
            />
          </QTableBody>
        </QTable>
        {total > 10 ? (
          <QAsyncTablePagination
            page={page + 1}
            pagination={{
              dataPerPage: pageSize,
              totalData: total,
              lastPage: totalPageCount,
            }}
            getPage={newPage => setPage(newPage - 1)}
            showRowsPerPage={true}
            currentRows={(page + 1) * pageSize > filteredManagedApps.length ? filteredManagedApps.length % pageSize : pageSize}
            handlePageSizeChange={handleChangeRowsPerPage}
            pageSize={pageSize}
            pageSizeOptions={[10, 20, 50, 100].map(v => ({ label: v, value: v }))}
          />
        ) : null}
      </StyledTableWrapper>
    </>
  );
}

const StyledTableWrapper = styled('div')`
  width: 100%;
`;

const StyledQButton = styled(QButton)`
  // align-self: flex-end;
  margin-left: auto;
  min-width: 110px;
`;

const StyledAvatar = styled(Avatar)`
  margin-right: 10px;
`;

const StyledKwTableCell = styled(QTableCell)`
  display: flex;
  flex-direction: row;
  align-items: center;
`;

const StyledFilterWrapper = styled('div')`
  display: flex;
  column-gap: 16px;
  margin-bottom: 30px;
  margin-top: 30px;
  align-items: center;
`;
